/* tslint:disable:max-file-line-count */

// Vendor
import * as React from 'react';
import { inject } from 'mobx-react';
import { Link } from 'react-router-dom';

// Bernie
import { AnalyticsStore, ExperimentStore, RouteStore } from 'bernie-core';
import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { ClickTracker } from 'bernie-analytics';
import { Context } from 'bernie-context';
import { ErrorBoundary, ViewLarge, ViewMedium, Viewport, ViewSmall } from 'bernie-view';
import { LocalizedText } from 'bernie-l10n';

// Lodging Stores
import { CurrentHotelStore } from 'stores/current-hotel-store';
import { SearchResultsStore } from 'stores/search-results-store';
import { ShortlistStore } from 'stores/shortlist-store';

// Models
import { DisclaimerViewModel, GalleryViewModel, HotelViewModel, PropertyListingBadgeModel } from 'models';

// UITK
import { UitkFigureAspectRatioType } from 'uitk-react-images';
import { UitkBadge, UitkBadgeTheme } from 'uitk-react-badge';
import { UitkCard, UitkCardContent, UitkCardImageLayout, UitkCardMedia } from 'uitk-react-cards';
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';
import { UitkMoreInfoTrigger } from 'uitk-react-more-info-trigger';
import { UitkRating, UitkRatingIcon } from 'uitk-react-rating';

// Utils
import { onClickScrollToTarget } from 'utils/smooth-scroll';

// Common components
import { Messaging } from 'components/common/messaging';
// Internals
import { AdditionalInfo } from './internals/additional-info';
import { AlternateDates } from './internals/alternate-dates';
import { CardLink } from './internals/card-link';
import { DisclaimerDialogBulletList } from 'components/common/disclaimer-dialog-bullet-list';
import { HighightedPropertyDetails } from './internals/highlighted-property-details';
import { ListingDescription } from './internals/listing-description';
import { ListingErrorBoundary } from './internals/listing-error-boundary';
import { ListingFooter } from './internals/footer';
import { ListingName } from './internals/listing-name';
import { ListingTravelAd } from './internals/listing-travel-ad';
import { Neighborhood } from './internals/neighborhood';
import { PlaceholderCard } from './internals/placeholder-card';
import { PriceAndSupportingMessagesContainer } from './internals/price-and-supporting-messages-container';
import { SearchResultsReviews } from './internals/search-results-reviews';
import { Title } from './internals/title';
import { TitleHeading } from './internals/title-heading';

const LinkTracked = ClickTracker(Link);

export interface ListingComponentProps {
  analytics?: AnalyticsStore;
  breakfastIncluded?: string;
  className: string;
  context?: Context;
  currentHotel?: CurrentHotelStore;
  'data-stid'?: string;
  dataAttributes?: any;
  endDate?: string;
  index?: number;
  isDatelessPdp?: boolean;
  gallery?: GalleryViewModel;
  lazyLoadImg?: boolean;
  loading?: boolean;
  searchResults?: SearchResultsStore;
  stars?: string;
  startDate?: string;
  source?: string;
  model: HotelViewModel;
  referralObject?: {
    navigation?: {
      linkName?: string;
      rfrr?: string;
    };
    favorite?: {
      linkName?: string;
      rfrr?: string;
    };
    unfavorite?: {
      linkName?: string;
      rfrr?: string;
    };
    close?: {
      linkName?: string;
      rfrr?: string;
    };
  };
  routeStore?: RouteStore;
  shortlist?: ShortlistStore;
  displayViewed?: boolean;
  experiment?: ExperimentStore;
  setResultRef?: (ref: HTMLElement, index: number) => void;
  defaultListingOnly?: boolean;
  disableGallery?: boolean;
}

interface ListingComponentState {
  alternateDatesExpanded: boolean;
}

@inject('routeStore', 'analytics', 'currentHotel')
class ListingComponent extends React.Component<ListingComponentProps, ListingComponentState> {
  constructor(props) {
    super(props);

    this.listingClickHandler = this.listingClickHandler.bind(this);
    this.setItemRef = this.setItemRef.bind(this);
    this.handleAdditionalInfoClick = this.handleAdditionalInfoClick.bind(this);
    this.routeStoreGoBack = this.routeStoreGoBack.bind(this);
  }

  private static alternateDatesInfo = {
    collapsedLinkName: 'HSR show alt dates',
    collapsedMsgKey: 'hotels.searchResults.alternateDates.more',
    collapsedRfrr: 'HOT:SR:ShowAltDates',
    expandedLinkName: 'HSR hide alt dates',
    expandedMsgKey: 'hotels.searchResults.alternateDates.less',
    expandedRfrr: 'HOT:SR:HideAltDates',
    headerTextKey: 'hotels.searchResults.alternateDates.try'
  };

  private handleAdditionalInfoClick() {
    onClickScrollToTarget({ scrollTo: 0 });
  }

  private setItemRef(ref) {
    const { index, setResultRef } = this.props;
    if (setResultRef && typeof setResultRef === 'function') {
      setResultRef(ref, index);
    }
  }

  private listingClickHandler(): void {
    const { currentHotel, model, searchResults } = this.props;
    const id = model.id;
    let hotel;
    if (searchResults && searchResults.hotels && searchResults.hotels.length > 0) {
      hotel = searchResults.hotels.find(h => h.id === id);
    }
    if (model && model.travelAd) {
      const travelAd = model.travelAd;
      if (travelAd.clickRedirectLinkoffTrackingUrl) {
        window.open(travelAd.clickRedirectLinkoffTrackingUrl);
      }
    }

    if (currentHotel && currentHotel.setSearchResultAsCurrentHotel && hotel) {
      currentHotel.setSearchResultAsCurrentHotel(hotel);

      return;
    }
  }

  private getTitleComponent(imageRatio = UitkFigureAspectRatioType.R21_9, extraProps = {}) {
    const { index, lazyLoadImg, model, referralObject, displayViewed, disableGallery, isDatelessPdp } = this.props;
    const {
      id,
      propertyListingBadgeModel,
      infositeUrl,
      infositeDatelessUrl,
      name,
      neighborhood,
      pinnedDetails,
      price,
      reviews,
      shortlistModel,
      starRatingIcon,
      thumbnail,
      travelAd
    } = model;
    const stars = isDatelessPdp ? model.comparisonModel && model.comparisonModel.starRating : model.stars;

    const { analytics, currentHotel } = this.props;
    let gallery = null;
    if (!disableGallery) {
      gallery = {
        images: model && model.gallery ? model.gallery.images : [],
        thumbnailGalleryEnabled: false
      };
    }
    const titleProps = {
      analytics,
      currentHotel,
      channel: pinnedDetails && pinnedDetails.channelName,
      className: 'listing__title',
      propertyListingBadgeModel,
      imageUrl: (thumbnail && thumbnail.src) || '',
      fallbackImageUrl: (thumbnail && thumbnail.fallBackImage) || '',
      gallery,
      index,
      lazyLoadImg,
      name,
      pinned: pinnedDetails,
      neighborhood,
      stars,
      starRatingIcon,
      travelAd,
      shortlistModel,
      infositeUrl: isDatelessPdp ? infositeDatelessUrl : infositeUrl,
      thumbnail,
      reviews,
      price,
      hotelId: id,
      referralObject,
      displaySecondaryBadge: !isDatelessPdp,
      displayViewed,
      ratio: imageRatio,
      listingClickHandler: this.listingClickHandler
    };

    return <Title {...titleProps} {...extraProps} />;
  }

  private routeStoreGoBack() {
    this.props.routeStore.goBack();
  }

  private getDefaultListingTopMarginClass(propertyListingBadgeModel: PropertyListingBadgeModel): string {
    const secondaryBadgeIconExists =
      propertyListingBadgeModel &&
      propertyListingBadgeModel.secondary &&
      (propertyListingBadgeModel.secondary.icon || propertyListingBadgeModel.secondary.text);

    return secondaryBadgeIconExists ? 'all-t-margin-half' : '';
  }

  public render() {
    const { className, defaultListingOnly, index, loading, model, routeStore, isDatelessPdp } = this.props;
    const {
      alternateDates,
      disclaimerDialog,
      hotelDescription,
      hotelId,
      listingFooter,
      loyaltyEarnAwards,
      name,
      neighborhood,
      pinnedDetails,
      price,
      primaryCardMessages,
      primaryCardMessagingModel,
      propertyListingBadgeModel,
      propertySummary,
      reviews,
      secondaryCardMessage,
      starRatingIcon,
      supportingMessages,
      thumbnail,
      travelAd,
      vipMessage,
      infositeUrl,
      infositeDatelessUrl,
      propertyTypeBadge, // Experiment: 32599 Property_Type_Badging
      recentlyViewed
    } = model;
    const secondary = propertyListingBadgeModel && propertyListingBadgeModel.secondary;
    const tertiary = propertyListingBadgeModel && propertyListingBadgeModel.tertiary;
    const lowerRight = propertyListingBadgeModel && propertyListingBadgeModel.lowerRight;
    const defaultListingTopMarginClass = this.getDefaultListingTopMarginClass(propertyListingBadgeModel);
    const stars = isDatelessPdp ? model.comparisonModel && model.comparisonModel.starRating : model.stars;
    const outerLiClassName = className;

    const listingWrapperProps = {
      className: outerLiClassName,
      'data-stid': this.props['data-stid'] || '',
      ref: this.setItemRef,
      tabIndex: -1
    };

    const alternateDatesProps = {
      handleClick: this.handleAdditionalInfoClick,
      model: alternateDates
    };
    const { loyaltyBaseEarnMessage, loyaltyBaseEarnAccessibilityMessage, loyaltyEarnIcon } = { ...loyaltyEarnAwards };

    const vipAndPropertyTypeBadgeProps = {
      vipMessage,
      propertyTypeBadge,
      recentlyViewed,
      isDatelessPdp
    };

    const priceAndSupportingMessagesContainerProps = {
      ...{ secondaryCardMessage, supportingMessages, price },
      wrapPrice: true,
      viewSmall: true,
      priceFontWeight: '600',
      hotelId: model.hotelId,
      recentlyViewed,
      isDatelessPdp
    };

    const listingNameProps = {
      name,
      ariaHidden: true,
      recentlyViewed
    };

    const highlightedPropertyDetailsProps = {
      propertyDetails: propertySummary
    };
    const cardLinkProps = { ...this.props };
    cardLinkProps.model.infositeUrl = isDatelessPdp ? infositeDatelessUrl : infositeUrl;

    const DefaultListing = (
      <li {...listingWrapperProps}>
        {loading ? (
          <PlaceholderCard index={index} />
        ) : (
          <UitkCard className="has-action listing__card">
            <ListingName name={name} visuallyHidden={true} />
            <div className="link-container">
              <ListingErrorBoundary thumbnail={thumbnail} name={name}>
                {this.getTitleComponent()}
              </ListingErrorBoundary>
              <UitkCardContent className={`all-y-padding-three all-x-padding-three listing-content ${defaultListingTopMarginClass}`}>
                <AbacusExperiment name="SRP_propertyName_SingleCol">
                  <AbacusControl>
                    <React.Fragment>
                      <VipAndPropertyTypeBadge {...vipAndPropertyTypeBadgeProps} />
                      <div className="uitk-grid">
                        <div className="uitk-cell all-cell-fill uitk-type-300">
                          <ListingName {...listingNameProps} />
                          <HighightedPropertyDetails {...highlightedPropertyDetailsProps} />
                          <Neighborhood neighborhood={neighborhood} />
                        </div>
                        <div className="uitk-cell all-cell-shrink uitk-type-right">
                          <PriceAndSupportingMessagesContainer {...priceAndSupportingMessagesContainerProps} />
                        </div>
                      </div>
                    </React.Fragment>
                  </AbacusControl>
                  <AbacusVariant bucket={1}>
                    <React.Fragment>
                      <div className="uitk-grid">
                        <div className="uitk-cell all-cell-fill uitk-type-300">
                          <ListingName {...listingNameProps} />
                        </div>
                      </div>
                      <VipAndPropertyTypeBadge {...vipAndPropertyTypeBadgeProps} />
                      <div className="uitk-grid">
                        <div className="uitk-cell all-cell-fill uitk-type-300">
                          <HighightedPropertyDetails {...highlightedPropertyDetailsProps} />
                          <Neighborhood neighborhood={neighborhood} />
                        </div>
                        <div className="uitk-cell all-cell-shrink uitk-type-right">
                          <PriceAndSupportingMessagesContainer {...priceAndSupportingMessagesContainerProps} />
                        </div>
                      </div>
                    </React.Fragment>
                  </AbacusVariant>
                </AbacusExperiment>
                <div className="uitk-grid">
                  <div className="uitk-cell all-cell-fill uitk-type-300">
                    {stars && (
                      <div className="all-b-padding-half">
                        <span className="is-visually-hidden">
                          <LocalizedText message="hotels.searchResults.filtersDialog.starRating.label" />
                        </span>
                        <UitkRating icon={UitkRatingIcon[starRatingIcon]} rating={String(stars)} />
                      </div>
                    )}
                    {loading ? null : (
                      <React.Fragment>
                        <ErrorBoundary label="hotel.search.listing.messaging">
                          <Messaging
                            className="listing__messaging uitk-grid uitk-type-300"
                            primaryMessaging={primaryCardMessagingModel}
                            primaryMessagingList={primaryCardMessages}
                          />
                        </ErrorBoundary>
                        {loyaltyBaseEarnMessage && (
                          <ErrorBoundary label="hotel.search.listing.baseEarn">
                            <div data-stid="hsr_loyalty_base_earn" className="listing__baseearn uitk-grid uitk-type-300">
                              {loyaltyEarnIcon && (
                                <UitkIcon
                                  name={loyaltyEarnIcon}
                                  className="uitk-cell all-cell-shrink all-r-padding-two"
                                  size={UitkIconSize.SMALL}
                                />
                              )}
                              {loyaltyBaseEarnAccessibilityMessage && (
                                <span className="is-visually-hidden">{loyaltyBaseEarnAccessibilityMessage}</span>
                              )}
                              <span
                                aria-hidden={Boolean(loyaltyBaseEarnAccessibilityMessage)}
                                className="pwa-theme--loyalty"
                                dangerouslySetInnerHTML={{ __html: loyaltyBaseEarnMessage }}
                              />
                            </div>
                          </ErrorBoundary>
                        )}
                      </React.Fragment>
                    )}
                  </div>
                  {lowerRight && !isDatelessPdp && <div className="uitk-cell all-cell-shrink all-cell-align-bottom">{lowerRight}</div>}
                </div>
                <BreakfastAndWifiDisclaimer
                  disclaimerDialog={disclaimerDialog}
                  handleBack={this.routeStoreGoBack}
                  hotelId={model.hotelId}
                  routeStore={routeStore}
                />
                <ListingDescription description={hotelDescription} />
                <ErrorBoundary label="hotel.search.listing.reviews">
                  <SearchResultsReviews className="listing__reviews all-t-margin-two" reviews={reviews} />
                </ErrorBoundary>
              </UitkCardContent>
              <CardLink {...cardLinkProps} index={index} listingClickHandler={this.listingClickHandler} />
            </div>
            <ListingFooter {...this.props} listingFooter={listingFooter} listingClickHandler={this.listingClickHandler} />
            {alternateDates && (
              <AdditionalInfo {...ListingComponent.alternateDatesInfo} displayHeaderText={false} keepOpen={false}>
                <AlternateDates {...alternateDatesProps} />
              </AdditionalInfo>
            )}
          </UitkCard>
        )}
      </li>
    );

    if (defaultListingOnly) {
      return DefaultListing;
    }

    const horizontalPriceAndSupportingMessagesContainerProps = {
      ...{ secondaryCardMessage, supportingMessages, price, secondary },
      viewSmall: false,
      wrapPrice: false,
      priceFontWeight: '600',
      hotelId: model.hotelId,
      recentlyViewed,
      isDatelessPdp
    };

    const HorizontalListing = imageAspectRatio => (
      <li {...listingWrapperProps}>
        {loading ? (
          <PlaceholderCard index={index} className="horizontal-placeholder" />
        ) : (
          <UitkCard imageLayout={UitkCardImageLayout.LEFT_FULLBLEED}>
            <ListingName name={name} visuallyHidden={true} />
            <div className="link-container uitk-grid">
              {pinnedDetails && pinnedDetails.heading && <TitleHeading headingInfo={pinnedDetails} />}
              <UitkCardMedia>
                <ListingErrorBoundary thumbnail={thumbnail} name={name}>
                  {this.getTitleComponent(imageAspectRatio, {
                    displaySecondaryBadge: false,
                    displayHeading: false
                  })}
                </ListingErrorBoundary>
              </UitkCardMedia>
              <UitkCardContent className="all-y-padding-three all-x-padding-three listing-content">
                <AbacusExperiment name="SRP_propertyName_SingleCol">
                  <AbacusControl>
                    <React.Fragment>
                      <div className="uitk-cell all-cell-fill uitk-type-300">
                        <VipAndPropertyTypeBadge {...vipAndPropertyTypeBadgeProps} />
                        <ListingName {...listingNameProps} />
                        <Stars stars={stars} starRatingIcon={starRatingIcon} />
                        <HighightedPropertyDetails {...highlightedPropertyDetailsProps} />
                        <Neighborhood neighborhood={neighborhood} />
                        <ListingTravelAd travelAd={travelAd} />
                      </div>
                      <PriceAndSupportingMessagesContainer {...horizontalPriceAndSupportingMessagesContainerProps} />
                    </React.Fragment>
                  </AbacusControl>
                  <AbacusVariant bucket={1}>
                    <div className="uitk-grid all-cell-align-top">
                      <div className="uitk-cell all-cell-fill uitk-type-300">
                        <ListingName {...listingNameProps} />
                      </div>
                      <div className="uitk-grid">
                        <div className="uitk-cell all-cell-fill uitk-type-300">
                          <VipAndPropertyTypeBadge {...vipAndPropertyTypeBadgeProps} />
                          <Stars stars={stars} starRatingIcon={starRatingIcon} />
                          <HighightedPropertyDetails {...highlightedPropertyDetailsProps} />
                          <Neighborhood neighborhood={neighborhood} />
                          <ListingTravelAd travelAd={travelAd} />
                        </div>
                        <PriceAndSupportingMessagesContainer {...horizontalPriceAndSupportingMessagesContainerProps} />}
                      </div>
                    </div>
                  </AbacusVariant>
                </AbacusExperiment>
                <div className="uitk-cell all-cell-align-bottom">
                  <div className="uitk-grid">
                    <div className="uitk-cell all-cell-fill all-cell-align-bottom">
                      {loading ? null : (
                        <>
                          <ErrorBoundary label="hotel.search.listing.messaging">
                            <Messaging
                              className="listing__messaging uitk-grid uitk-type-300"
                              primaryMessaging={primaryCardMessagingModel}
                              primaryMessagingList={primaryCardMessages}
                            />
                          </ErrorBoundary>
                          {loyaltyBaseEarnMessage && (
                            <ErrorBoundary label="hotel.search.listing.baseEarn">
                              <div data-stid="hsr_loyalty_base_earn" className="listing__baseearn uitk-grid uitk-type-300">
                                {loyaltyEarnIcon && (
                                  <UitkIcon
                                    name={loyaltyEarnIcon}
                                    className="uitk-cell all-cell-shrink all-r-padding-two"
                                    size={UitkIconSize.SMALL}
                                  />
                                )}
                                {loyaltyBaseEarnAccessibilityMessage && (
                                  <span className="is-visually-hidden">{loyaltyBaseEarnAccessibilityMessage}</span>
                                )}
                                <span
                                  aria-hidden={Boolean(loyaltyBaseEarnAccessibilityMessage)}
                                  className="pwa-theme--loyalty"
                                  dangerouslySetInnerHTML={{ __html: loyaltyBaseEarnMessage }}
                                />
                              </div>
                            </ErrorBoundary>
                          )}
                        </>
                      )}
                      <BreakfastAndWifiDisclaimer
                        disclaimerDialog={disclaimerDialog}
                        handleBack={this.routeStoreGoBack}
                        hotelId={hotelId}
                        routeStore={routeStore}
                      />
                      <ListingDescription description={hotelDescription} />
                      <ErrorBoundary label="hotel.search.listing.reviews">
                        <SearchResultsReviews className="listing__reviews all-t-margin-two" reviews={reviews} />
                      </ErrorBoundary>
                    </div>
                    <div className="uitk-cell all-cell-shrink all-cell-align-bottom uitk-type-right">
                      {tertiary &&
                        tertiary.text && (
                          <div className="uitk-cell all-x-gutter-two uitk-type-right all-cell-shrink">
                            {!isDatelessPdp && <UitkBadge theme={UitkBadgeTheme[tertiary.theme]} text={tertiary.text} />}
                          </div>
                        )}
                      {lowerRight && !isDatelessPdp && <div data-stid="lower-right">{lowerRight}</div>}
                    </div>
                  </div>
                </div>
              </UitkCardContent>
              <CardLink {...this.props} index={index} listingClickHandler={this.listingClickHandler} />
            </div>
            <ListingFooter {...this.props} listingFooter={listingFooter} listingClickHandler={this.listingClickHandler} />
            {alternateDates && (
              <AdditionalInfo {...ListingComponent.alternateDatesInfo} displayHeaderText={true} keepOpen={true}>
                <AlternateDates {...alternateDatesProps} />
              </AdditionalInfo>
            )}
          </UitkCard>
        )}
      </li>
    );

    return (
      <Viewport>
        <ViewSmall>{DefaultListing}</ViewSmall>
        <ViewMedium>{HorizontalListing(UitkFigureAspectRatioType.R3_2)}</ViewMedium>
        <ViewLarge>{HorizontalListing(UitkFigureAspectRatioType.R16_9)}</ViewLarge>
      </Viewport>
    );
  }
}

interface DisclaimerProps {
  disclaimerDialog: DisclaimerViewModel;
  handleBack?: () => void;
  hotelId?: number;
  routeStore?: RouteStore;
}

const VipAndPropertyTypeBadge = ({ vipMessage, propertyTypeBadge, recentlyViewed, isDatelessPdp }) =>
  (vipMessage || propertyTypeBadge || recentlyViewed) && !isDatelessPdp ? (
    <div className="uitk-cell all-cell-1-1 all-b-margin-one">
      {recentlyViewed && <RecentlyViewed recentlyViewed={recentlyViewed} addSeparator={vipMessage || propertyTypeBadge} />}
      {vipMessage && <span className="uitk-type-300 uitk-type-medium listing-vip">{vipMessage}</span>}
      {vipMessage && propertyTypeBadge && <span className="uitk-type-300 uitk-type-bold all-x-margin-two">&#183;</span>}
      {propertyTypeBadge && (
        <span className="uitk-type-regular pwa-theme--grey-700 uitk-type-300 listing-structure-type">{propertyTypeBadge}</span>
      )}
    </div>
  ) : null;

const RecentlyViewed = ({ recentlyViewed, addSeparator }) => (
  <>
    {recentlyViewed.icon && (
      <UitkIcon
        name={recentlyViewed.icon}
        size={UitkIconSize.SMALL}
        className="pwa-theme--grey-700 all-cell-align-middle all-b-padding-half"
      />
    )}
    <span className="uitk-type-regular pwa-theme--grey-700 uitk-type-300 listing-recently-viewed">{recentlyViewed.message}</span>
    {addSeparator && <span className="uitk-type-300 uitk-type-bold all-x-margin-two">&#183;</span>}
  </>
);

const BreakfastAndWifiDisclaimer = ({ disclaimerDialog, handleBack, hotelId, routeStore }: DisclaimerProps) => {
  if (!disclaimerDialog) {
    return null;
  }

  const { disclaimerContent, disclaimerLink } = disclaimerDialog;
  const dialogId = hotelId ? `BreakfastAndWifiDisclaimerDialog-${hotelId}` : 'BreakfastAndWifiDisclaimerDialog';
  const dialogQuery = routeStore.mergeQueryString(routeStore.search, { pwaDialog: dialogId });

  return (
    <div className="uitk-grid all-t-padding-two" data-stid="breakfast-wifi-disclaimer">
      <UitkMoreInfoTrigger>
        <LinkTracked
          linkName="Breakfast and Wifi Disclaimer Dialog"
          rfrr=".BreakfastAndWifi.DisclaimerDialog"
          to={{ pathname: routeStore.pathname, search: dialogQuery }}
        >
          <div className="uitk-cell uitk-type-300 pwa-theme--grey-700">
            <span className="breakfast-and-wifi-disclaimer-link-text">{disclaimerLink}</span>
          </div>
          <UitkIcon name="info_outline" className="pwa-theme--grey-700 all-r-padding-half" />
        </LinkTracked>
      </UitkMoreInfoTrigger>
      {disclaimerContent && (
        <DisclaimerDialogBulletList
          dialogId={dialogId}
          dialogType="pwaDialog"
          disclaimerText={disclaimerContent}
          handleClose={handleBack}
          layerId="breakfast-and-wifi-disclaimer"
          disclaimerTextId="breakfast-and-wifi-disclaimer-dialog-text"
        />
      )}
    </div>
  );
};

interface StarsProps {
  stars: number;
  starRatingIcon: string;
}
const Stars = ({ stars, starRatingIcon }: StarsProps) =>
  stars ? (
    <div className="all-b-padding-half">
      <span className="is-visually-hidden">
        <LocalizedText message={'hotels.searchResults.filtersDialog.starRating.label'} />
      </span>
      <UitkRating icon={UitkRatingIcon[starRatingIcon]} rating={String(stars)} />
    </div>
  ) : null;

export const Listing = inject('searchResults', 'currentHotel', 'context', 'shortlist', 'analytics', 'experiment')(ListingComponent);
